import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney, PAY_METHODS, STATUS_BADGE, STATUS_LABEL } from "@/lib/invoicing";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Ban, Copy, Download, Loader2, Pencil, Send, Trash2, UploadCloud,
} from "lucide-react";

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState("");
  const [sendDlg, setSendDlg] = useState(false);
  const [extraMsg, setExtraMsg] = useState("");
  const [payDlg, setPayDlg] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "eft", reference: "", paid_at: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoicing/invoices/${invoiceId}`);
      setInv(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoice");
    }
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const sendInvoice = () => run("send", async () => {
    await api.post(`/invoicing/invoices/${invoiceId}/send`, {
      origin_url: window.location.origin,
      extra_message: extraMsg || null,
    });
    setSendDlg(false); setExtraMsg("");
  }, "Invoice emailed to client");

  const recordPayment = () => run("pay", async () => {
    await api.post(`/invoicing/invoices/${invoiceId}/payments`, {
      amount: Number(pay.amount), method: pay.method,
      reference: pay.reference || null,
      paid_at: pay.paid_at ? new Date(pay.paid_at).toISOString() : null,
    });
    setPayDlg(false);
  }, "Payment recorded");

  const pushXero = () => run("xero", async () => {
    const { data } = await api.post(`/invoicing/invoices/${invoiceId}/push-to-xero`);
    if (data.warnings?.length) data.warnings.forEach((w) => toast.warning(w));
  }, "Pushed to Xero");

  const voidInvoice = () => {
    if (!window.confirm("Void this invoice?")) return;
    run("void", () => api.post(`/invoicing/invoices/${invoiceId}/void`), "Invoice voided");
  };

  const downloadPdf = () => run("pdf", async () => {
    const { data } = await api.get(`/invoicing/invoices/${invoiceId}/pdf`,
      { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  });

  const copyPayLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/pay/${inv.public_token}`);
    toast.success("Pay link copied");
  };

  const deletePayment = (p) => {
    if (!window.confirm(`Remove ${fmtMoney(p.amount)} payment?`)) return;
    run("delpay", () =>
      api.delete(`/invoicing/invoices/${invoiceId}/payments/${p.payment_id}`),
      "Payment removed");
  };

  if (!inv) return <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />;
  const open = inv.status !== "voided";
  const hasBalance = inv.balance_due > 0.005;

  return (
    <div className="space-y-5 max-w-5xl" data-testid="invoice-detail-page">
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/invoicing"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button></Link>
        <h1 className="text-2xl font-bold">{inv.invoice_number}</h1>
        <Badge variant="outline" className={STATUS_BADGE[inv.status]} data-testid="invoice-status-badge">
          {STATUS_LABEL[inv.status]}
        </Badge>
        {inv.xero_pushed && (
          <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">In Xero</Badge>
        )}
        <div className="flex-1" />
        {open && (
          <>
            <Button variant="outline" size="sm" onClick={() => setSendDlg(true)}
              data-testid="send-invoice-btn">
              <Send className="h-4 w-4 mr-1" /> {inv.sent_at ? "Resend" : "Send"}
            </Button>
            {hasBalance && (
              <Button size="sm" data-testid="record-payment-btn"
                onClick={() => { setPay({ amount: inv.balance_due.toFixed(2), method: "eft", reference: "", paid_at: "" }); setPayDlg(true); }}>
                Record payment
              </Button>
            )}
            {!inv.xero_pushed && inv.status !== "draft" && (
              <Button variant="outline" size="sm" onClick={pushXero}
                disabled={busy === "xero"} data-testid="push-xero-btn">
                {busy === "xero" ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  : <UploadCloud className="h-4 w-4 mr-1" />} Push to Xero
              </Button>
            )}
            {!inv.xero_pushed && (
              <Button variant="outline" size="sm" data-testid="edit-invoice-btn"
                onClick={() => nav(`/invoicing/invoices/${invoiceId}/edit`)}>
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {inv.amount_paid <= 0 && (
              <Button variant="outline" size="sm" onClick={voidInvoice}
                data-testid="void-invoice-btn">
                <Ban className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </>
        )}
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-pdf-btn">
          <Download className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={copyPayLink} data-testid="copy-pay-link-btn">
          <Copy className="h-4 w-4 mr-1" /> Pay link
        </Button>
      </div>

      {inv.xero_push_error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"
          data-testid="xero-push-error">
          Xero push error: {inv.xero_push_error}
        </div>
      )}
      {(inv.xero_push_warnings || []).map((w) => (
        <div key={w} className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          {w}
        </div>
      ))}

      <div className="grid md:grid-cols-3 gap-3">
        <Card><CardContent className="p-4 text-sm space-y-1">
          <div className="text-xs text-zinc-500">Bill to</div>
          <div className="font-medium">{inv.client_name}</div>
          <div className="text-zinc-500">{(inv.bill_to_emails || []).join(", ") || "no email"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-sm space-y-1">
          <div className="text-xs text-zinc-500">Issued from</div>
          <div className="font-medium">{inv.xero_tenant_name || "Default organisation"}</div>
          <div className="text-zinc-500">Issued {inv.issue_date} · Due {inv.due_date}</div>
          {inv.reference && <div className="text-zinc-500">Ref: {inv.reference}</div>}
        </CardContent></Card>
        <Card><CardContent className="p-4 text-sm space-y-1">
          <div className="text-xs text-zinc-500">Balance due</div>
          <div className="text-2xl font-bold" data-testid="invoice-balance-due">
            {fmtMoney(inv.balance_due)}
          </div>
          <div className="text-zinc-500">
            {fmtMoney(inv.amount_paid)} paid of {fmtMoney(inv.total)}
          </div>
        </CardContent></Card>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
              <th className="p-3">Description</th><th className="p-3 text-right">Qty</th>
              <th className="p-3 text-right">Unit price</th><th className="p-3">GST</th>
              <th className="p-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.line_items.map((li, i) => (
              <tr key={`${li.description}-${i}`} className="border-b">
                <td className="p-3">{li.description}</td>
                <td className="p-3 text-right">{li.quantity}</td>
                <td className="p-3 text-right">{fmtMoney(li.unit_price)}</td>
                <td className="p-3">{li.gst ? "10%" : "Free"}</td>
                <td className="p-3 text-right">{fmtMoney(li.line_total)}</td>
              </tr>
            ))}
            <tr><td colSpan={4} className="p-2 pr-4 text-right text-zinc-500">Subtotal</td>
              <td className="p-2 pr-3 text-right">{fmtMoney(inv.subtotal)}</td></tr>
            <tr><td colSpan={4} className="p-2 pr-4 text-right text-zinc-500">GST 10%</td>
              <td className="p-2 pr-3 text-right">{fmtMoney(inv.gst_total)}</td></tr>
            <tr className="font-bold"><td colSpan={4} className="p-2 pr-4 text-right">Total</td>
              <td className="p-2 pr-3 text-right">{fmtMoney(inv.total)}</td></tr>
          </tbody>
        </table>
      </CardContent></Card>

      <Card><CardContent className="p-4">
        <div className="font-semibold text-sm mb-2">Payments</div>
        {!inv.payments?.length && <div className="text-sm text-zinc-400">No payments yet.</div>}
        {inv.payments?.map((p) => (
          <div key={p.payment_id}
            className="flex items-center gap-3 border-b last:border-0 py-2 text-sm"
            data-testid={`payment-row-${p.payment_id}`}>
            <span className="w-24">{(p.paid_at || "").slice(0, 10)}</span>
            <Badge variant="outline" className="uppercase text-xs">{p.method}</Badge>
            <span className="text-zinc-500 flex-1 truncate">{p.reference || "—"}</span>
            {p.pushed_to_xero && <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-xs">Xero</Badge>}
            <span className="font-medium">{fmtMoney(p.amount)}</span>
            {p.method !== "stripe" && !p.pushed_to_xero && (
              <Button variant="ghost" size="sm" onClick={() => deletePayment(p)}>
                <Trash2 className="h-4 w-4 text-red-400" />
              </Button>
            )}
          </div>
        ))}
      </CardContent></Card>

      <Dialog open={sendDlg} onOpenChange={setSendDlg}>
        <DialogContent data-testid="send-dialog">
          <DialogHeader><DialogTitle>Send invoice {inv.invoice_number}</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-500">
            To: {(inv.bill_to_emails || []).join(", ") || "⚠ no recipient emails on this invoice"}
          </div>
          <Textarea rows={3} placeholder="Optional message to include in the email…"
            value={extraMsg} onChange={(e) => setExtraMsg(e.target.value)} />
          <div className="text-xs text-zinc-400">
            Sends a branded email with the PDF attached plus a secure "View &amp; Pay" link.
            {inv.push_without_payment && " This invoice is flagged to push to Xero immediately on send."}
          </div>
          <DialogFooter>
            <Button onClick={sendInvoice} disabled={busy === "send" || !inv.bill_to_emails?.length}
              data-testid="send-confirm-btn">
              {busy === "send" && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payDlg} onOpenChange={setPayDlg}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-medium text-zinc-500 mb-1">
                Amount (balance {fmtMoney(inv.balance_due)}) — part payments OK
              </div>
              <Input type="number" step="0.01" value={pay.amount}
                data-testid="payment-amount-input"
                onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
            </div>
            <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAY_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Reference (receipt #, transfer description…)"
              value={pay.reference}
              onChange={(e) => setPay({ ...pay, reference: e.target.value })} />
            <Input type="date" value={pay.paid_at}
              onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} />
            <div className="text-xs text-zinc-400">
              A receipt email is sent automatically. When the balance reaches $0 the
              invoice is pushed to Xero as paid.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={recordPayment} data-testid="payment-confirm-btn"
              disabled={busy === "pay" || !Number(pay.amount)}>
              {busy === "pay" && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
